import React, {useCallback, useEffect, useState} from 'react';
import {IconDelete, IconEdit, IconFilter, IconPlus, IconSend, IconSpinner, IconWarning, IconX} from "../../../../components/icons";
import {useNavigate} from "react-router-dom";
import {deleteUtilisateur, getUtilisateurRoles, getUtilisateurs, reSendConfirmUserPassword} from "../../../../components/requests";
import {Utilisateur, UtilisateurRole} from "../../../../components/models";
import Pagination from "../../components/Pagination";
import Modal from "../../../../components/common/Modal";
import {toast} from "react-toastify";
import {useAuth} from "../../../contexts/AuthContext";
import PartenaireBadge from "../../../../components/common/PartenaireBadge";
import {useFormik} from 'formik';
import {formatDate} from "../../../../components/helpers";

const UtilisateurPage = () => {
  const {currentUser, isAdmin, logout, canWrite} = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(true);
  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [utilisateurRoles, setUtilisateurRoles] = useState<UtilisateurRole[]>([]);
  const [utilisateurs, setUtilisateurs] = useState<Utilisateur[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [utilisateurRoleQuery, setUtilisateurRoleQuery] = useState<string>('');
  const [openModal, setOpenModal] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [idSending, setIdSending] = useState<number | null>(null);

  const getListUtilisateurRoles = useCallback(() => {
    getUtilisateurRoles()
      .then(response => {
        if (response.status === 200 && response.data !== undefined) {
          setUtilisateurRoles(response.data.member);
        }
      })
      .catch(error => console.error(error))
      .finally(() => setIsFilterLoading(false));
  }, []);

  // Méthode de liste des utilisateurs avec pagination
  const getListUtilisateurs = useCallback(() => {
    setIsListLoading(true);
    getUtilisateurs(page, searchQuery, utilisateurRoleQuery)
      .then((response) => {
        if (response.status === 200 && response.data !== undefined) {
          setTotalItems(response.data?.totalItems);
          setUtilisateurs(response.data?.data);
        }
      })
      .catch((error) => {
        console.error(error);
        if (error.status === 401) logout();
      })
      .finally(() => {
        setIsLoading(false);
        setIsListLoading(false);
      });
  }, [
    page,
    searchQuery,
    utilisateurRoleQuery,
    logout
  ]);

  // Méthode de suppression d'un utilisateur
  const removeUtilisateur = (id: number) => {
    setIsDeleting(true);
    deleteUtilisateur(id)
      .then((response) => {
        if (response.status === 200) {
          setUtilisateurs(utilisateurs.filter(utilisateur => utilisateur.id !== id));
          setTotalItems(totalItems - 1);
          toast.success('Utilisateur supprimé');
          if (currentUser.id === id) logout();
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsDeleting(false);
        setOpenModal(null);
      })
  };

  // méthode pour renvoyer le mail de confirmation de modification de mot de passe
  const reSendEmailConfirmation = (id: number) => {
    setIdSending(id);
    reSendConfirmUserPassword(id)
      .then((response) => {
        if (response.status === 200) {
          toast.success('Email envoyé');
        }
      })
      .catch((error) => console.error(error))
      .finally(() => setIdSending(null))
  }

  const formik = useFormik({
    initialValues: {
      search: '',
      utilisateurRole: '',
    },
    onSubmit: values => {
      setSearchQuery(values?.search?.trim());
      setUtilisateurRoleQuery(values?.utilisateurRole?.trim());
    },
  });

  useEffect(() => {
    getListUtilisateurRoles();
  }, [getListUtilisateurRoles]);

  // useEffect pour afficher la liste et activer la pagination
  useEffect(() => {
    if (isAdmin) getListUtilisateurs();
    else navigate('/accueil');
  }, [getListUtilisateurs, isAdmin, navigate]);

  if (isLoading || isFilterLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen-fit">
        <IconSpinner className="animate-spin w-10 h-10"/>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen-fit p-4 animate-in fade-in duration-300">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Tous les utilisateurs</h3>
          <p className="text-sm text-gray-500">{totalItems} utilisateurs au total</p>
        </div>

        {canWrite('Parametrage') && (
          <div className="flex gap-4">
            <div className="flex items-end">
              <div>
                <button
                  onClick={() => navigate('creer')}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  <IconPlus/>
                  Créer un utilisateur
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="flex justify-end mb-2">
          <div className="flex gap-4 w-full">
            <div className="flex-1">
              <label className="form-label">Recherche</label>
              <input
                name="search"
                type="text"
                className="w-full form-input"
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-1">
              <label className="form-label">Rôle</label>
              <select
                name="utilisateurRole"
                className="w-full form-input form-select"
                onChange={formik.handleChange}
                value={formik.values.utilisateurRole}
              >
                <option value=""></option>
                {(utilisateurRoles.length > 0) && utilisateurRoles.map((utilisateurRole, key) =>
                  <option key={key} value={utilisateurRole.id}>{utilisateurRole.libelle}</option>
                )}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <IconFilter/>
                Filtrer
              </button>
            </div>
          </div>
        </div>
      </form>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
          <tr>
            <th className="px-5 py-3 text-left font-semibold">Nom</th>
            <th className="px-5 py-3 text-left font-semibold">Prénom</th>
            <th className="px-5 py-3 text-left font-semibold">Email</th>
            <th className="px-5 py-3 text-left font-semibold">Nom d'utilisateur</th>
            <th className="px-5 py-3 text-left font-semibold">Rôle</th>
            <th className="px-5 py-3 text-left font-semibold">Partenaires</th>
            <th className="px-5 py-3 text-left font-semibold">2FA</th>
            <th className="px-5 py-3 text-left font-semibold">Dernière connexion</th>
            <th className="px-5 py-3 text-left font-semibold">Statut</th>
            {canWrite('Parametrage') &&
              <th className="px-5 py-3 text-left font-semibold">Action</th>
            }
          </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
          {isListLoading ? (
            <tr>
              <td colSpan={12}>
                <div className="flex items-center justify-center min-h-[300px]">
                  <IconSpinner className="animate-spin w-10 h-10"/>
                </div>
              </td>
            </tr>
          ) : (utilisateurs?.length === 0) ? (
            <tr>
              <td colSpan={12}>
                <div className="flex items-center justify-center min-h-[300px]">
                  <p className="text-gray-500">Pas de résultat</p>
                </div>
              </td>
            </tr>
          ) : utilisateurs?.map((utilisateur) => {
            return (
              <tr key={utilisateur?.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-5 py-4">
                  <p className="font-medium text-gray-900 text-sm">
                    {utilisateur?.nom}
                  </p>
                </td>
                <td className="px-5 py-4">
                  <p className="font-medium text-gray-900 text-sm">
                    {utilisateur?.prenom}
                  </p>
                </td>
                <td className="px-5 py-4">
                  <p className="text-gray-500 font-mono text-sm">
                    {utilisateur?.email}
                  </p>
                </td>
                <td className="px-5 py-4">
                  <p className="font-medium text-gray-900 text-sm">
                    {utilisateur?.nomUtilisateur}
                  </p>
                </td>
                <td className="px-5 py-4">
                  <p className="font-medium text-gray-900 text-sm">
                    {utilisateur?.utilisateurRole?.libelle && (
                      <span className="inline-flex px-2.5 py-1 mr-2 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                          {utilisateur?.utilisateurRole?.libelle}
                        </span>
                    )}
                  </p>
                </td>
                <td className="px-5 py-4">
                  <div className="font-medium text-gray-900 text-sm">
                    {utilisateur?.partenaires && utilisateur?.partenaires?.length > 0 ?
                      utilisateur?.partenaires?.map((partenaire, key: number) =>
                        <PartenaireBadge key={key} nom={partenaire?.partenaire?.nom}/>
                      ) : null}
                  </div>
                </td>
                <td className="px-5 py-4">
                  {utilisateur?.twoFactorRequired ? (
                    <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                      {utilisateur?.twoFactorEnabled ? 'Actif' : 'Requis'}
                    </span>
                  ) : (
                    <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                      Non requis
                    </span>
                  )}
                </td>
                <td className="px-5 py-4">
                  <p className="text-gray-500 font-mono text-sm">
                    {utilisateur?.lastLogin ? formatDate(utilisateur?.lastLogin, true) : ''}
                  </p>
                </td>
                <td className="px-5 py-4">
                  {utilisateur?.actif ? utilisateur?.invite ? (
                    <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                      Invité
                    </span>
                  ) : (
                    <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                      Actif
                    </span>
                  ) : (
                    <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                      Invité
                    </span>
                  )}
                </td>
                {canWrite('Parametrage') &&
                  <td className="px-5 py-4">
                    <div className="flex justify-center items-center gap-1">
                      {utilisateur.invite === true && (
                        <div className="relative group">
                          <button
                            disabled={!(isAdmin || currentUser.id === utilisateur.id)}
                            onClick={() => (isAdmin || currentUser.id === utilisateur.id) ? reSendEmailConfirmation(utilisateur.id) : null}
                            className={`w-7 h-7 rounded text-xs font-medium flex justify-center items-center ${(isAdmin || currentUser.id === utilisateur.id) ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:cursor-not-allowed'}`}
                          >
                            {idSending && idSending === utilisateur.id ? <IconSpinner className="animate-spin w-4 h-4"/> : <IconSend/>}
                          </button>
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white">
                            Renvoyer l'email de confirmation
                          </span>
                        </div>
                      )}
                      <button
                        disabled={!(isAdmin || currentUser.id === utilisateur.id)}
                        onClick={() => (isAdmin || currentUser.id === utilisateur.id) ? navigate(`${utilisateur.id}/modifier`, {state: {user: utilisateur}}) : null}
                        className={`w-7 h-7 rounded text-xs font-medium flex justify-center items-center ${(isAdmin || currentUser.id === utilisateur.id) ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:cursor-not-allowed'}`}
                      >
                        <IconEdit/>
                      </button>
                      <button
                        disabled={!(isAdmin || currentUser.id === utilisateur.id)}
                        onClick={() => (isAdmin || currentUser.id === utilisateur.id) ? setOpenModal(utilisateur.id) : null}
                        className={`w-7 h-7 rounded text-xs font-medium flex justify-center items-center ${(isAdmin || currentUser.id === utilisateur.id) ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:cursor-not-allowed'}`}
                      >
                        <IconDelete/>
                      </button>
                    </div>
                  </td>}
              </tr>
            )
          })}
          </tbody>
        </table>
      </div>

      {!isListLoading && (
        <div className="pt-4">
          <Pagination totalItems={totalItems} step={25} page={page} setPage={setPage}/>
        </div>
      )}

      <Modal openModal={openModal}>
        <div className="section-header flex justify-between">
          <div className="flex items-center gap-3">
            <div className="section-icon bg-red-100 text-red-600">
              <IconWarning/>
            </div>
            <span className="section-title">Supprimer l'utilisateur</span>
          </div>
          <button onClick={() => setOpenModal(null)}>
            <IconX/>
          </button>
        </div>

        <div className="section-body p-3 text-sm">
          <p className="text-gray-600">Êtes-vous sûr de vouloir supprimer cet utilisateur ?</p>
          <p className="text-red-600">Cette action est irréversible.</p>
        </div>

        <div className="section-footer flex justify-end">
          <button
            onClick={() => setOpenModal(null)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            Annuler
          </button>
          <button
            onClick={() => removeUtilisateur(openModal)}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
            {isDeleting ? 'Suppression...' : 'Valider'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default UtilisateurPage;